import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import * as styles from '../FirstResponder.module.css';
import PreviewPagination from './PreviewPagination';
import { renderText } from './liveMetricDisplay';
import {
  CHECK_LABELS,
  LiveExclusionGroup,
  LiveFunnel,
  LiveFunnelStage,
  LiveJobDetail,
  LivePage,
  LivePersistedCheck,
  LiveRankingExplainer,
  LiveTone,
  LiveWorkerRow,
} from './liveViewModel';
import { formatCountdown } from './useCountdown';
import { useModalA11y } from './useModalA11y';

/*
 * Job drilldown modal over ONE RECORDED RUN. Two sections: Overview (typed
 * check evidence, the recorded wave, timeline) and Workers available (the
 * paginated after-gates snapshot as persisted for the run).
 *
 * Immutability: everything here is recorded history identified by run id,
 * evaluated timestamp and the policy version persisted at run time. Opening
 * the modal never recalculates a pool, and activating a new draft policy
 * never re-ranks anything shown here.
 *
 * Pagination: the workers table consumes a server-shaped page supplied by the
 * parent (fixture adapter today, API later). This component never slices a
 * full collection.
 */

const BADGE_TONE: Record<LiveTone, string | undefined> = {
  blue: styles.blue,
  amber: styles.amber,
  green: styles.green,
  red: styles.red,
  plain: undefined,
};

const Funnel = ({
  funnel,
  stage,
}: {
  funnel: LiveFunnel;
  stage: LiveFunnelStage | null;
}) => {
  if (!funnel) return null;
  const seg = (active: boolean, node: React.ReactNode) => (
    <span className={active ? styles.funnelActive : undefined}>{node}</span>
  );
  return (
    <div className={styles.funnel} data-testid="flp-funnel">
      {seg(
        stage === 'discovered',
        <span>
          <b>{funnel.poolSize ?? '—'}</b> workers discovered
        </span>,
      )}
      <span className={styles.arrow}>→</span>
      {seg(
        stage === 'after_gates',
        <span>
          <b>{funnel.passedGates ?? '—'}</b> workers available after gates
        </span>,
      )}
      <span className={styles.arrow}>→</span>
      {seg(
        stage === 'selected',
        <span>
          <b>{funnel.selectedCount}</b> selected
        </span>,
      )}
    </div>
  );
};

// Typed observed evidence -> sentence fragments. The backend sends facts;
// this component owns the words. A missing observation renders nothing extra
// (never a manufactured zero).
const observedText = (c: LivePersistedCheck): string | null => {
  const { kind, value } = c.observed;
  if (value === null) return null;
  switch (kind) {
    case 'duration_min':
      return `${value} min observed`;
    case 'timestamp':
      return c.key === 'no_ops_offer' && !c.passed
        ? `Ops offered at ${value}`
        : `${value}`;
    case 'count':
      return `${value}`;
    default:
      return null;
  }
};

/*
 * Task 11: an unsupported gate is one that was NOT APPLIED to this job, so there is no count to
 * report — that is a fact about the run, not a gap in the API, and the list now says so. The old
 * copy ("proposed contract field; the current backend does not report this count") was written
 * when the backend reported no gate counts at all; it now reports every applied gate, zero
 * included. tax_type_mismatch is the one permanent exception: it is applied before discovery
 * begins, so it genuinely excludes workers but can never be counted here.
 */
const unsupportedExclusionText = (g: LiveExclusionGroup): string =>
  g.key === 'tax_type_mismatch'
    ? 'applied before discovery, not counted'
    : 'not applied to this job';

/*
 * A worker row links to the worker page (/workers/:id, the convention every other worker link in
 * TenantAdmin uses) only when its id is a real worker id. The connected adapter always emits the
 * numeric worker_id; the dev-only preview fixtures carry synthetic ids such as "w-j1-01" that
 * have no page behind them, and those stay plain text rather than becoming a dead link.
 */
const hasWorkerPage = (id: string): boolean => /^\d+$/.test(id);

type Props = {
  detail: LiveJobDetail;
  // GRAVY-10373 Task 7: candidatesTotal is the wire's own workers-endpoint total (distinct from
  // detail.workersAvailableTotal, which comes from the job-detail endpoint) — null renders Unknown.
  workersPage: LivePage<LiveWorkerRow> & { candidatesTotal: number | null };
  onWorkersPageChange: (page: number) => void;
  ranking: LiveRankingExplainer;
  elapsed: number;
  onClose: () => void;
  initialSection?: 'overview' | 'workers';
};

const JobModal = ({
  detail,
  workersPage,
  onWorkersPageChange,
  ranking,
  elapsed,
  onClose,
  initialSection = 'overview',
}: Props) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [section, setSection] = useState<'overview' | 'workers'>(
    initialSection,
  );
  const [openSignals, setOpenSignals] = useState<Record<number, boolean>>({});
  useModalA11y(true, onClose, dialogRef);

  const evidence = detail.checks[0] ?? null;

  /*
   * Task 11: the workers endpoint's own candidates_total is the after-gates snapshot COUNT, and
   * older runs have none recorded. The job-detail endpoint's funnel still knows how many workers
   * passed the gates for that run, so rather than the bare "Unknown" the header says what IS known
   * and why the two numbers can differ. Only the snapshot total is missing — not the count itself.
   */
  const passedGates = detail.funnel?.passedGates ?? null;
  const candidatesTotalText =
    workersPage.candidatesTotal !== null
      ? String(workersPage.candidatesTotal)
      : passedGates !== null
        ? `${passedGates} after gates (snapshot not recorded for this run)`
        : 'Unknown';

  return (
    <div
      className={styles.overlay}
      data-testid="flp-job-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="flp-job-title"
        ref={dialogRef}
        tabIndex={-1}
        data-testid="flp-job-modal"
      >
        <div className={styles.modalHeader}>
          <div>
            {/* The title deep-links to the real job page (same /jobs/:jobId
                convention as the Shadow JobsTable). The jobs-table row itself
                only ever opens this modal — the outbound jump lives here. */}
            <h3 id="flp-job-title">
              {/*
                Hover and keyboard focus are styled in CSS so both get the same
                visible affordance. Pointer-only hover handlers never fire for a
                keyboard user, which left tab navigation with no link cue.
              */}
              <Link
                to={`/jobs/${detail.jobId}`}
                data-testid="flp-job-title-link"
                title={`Open job #${detail.jobId}`}
                className={styles.jobTitleLink}
              >
                {detail.title}
              </Link>
            </h3>
            <div
              className={`${styles.muted} ${styles.small}`}
              style={{ marginTop: 4 }}
            >
              {detail.client} · Shift starts {renderText(detail.shiftStartsAt)}{' '}
              · First Responder trigger {renderText(detail.actsAt)}
            </div>
          </div>
          <button
            type="button"
            className={styles.x}
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className={styles.section} style={{ paddingBottom: 0 }}>
          <div
            className={styles.filters}
            role="tablist"
            aria-label="Job detail sections"
          >
            {(
              [
                ['overview', 'Overview'],
                ['workers', 'Workers available'],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={section === key}
                className={`${styles.chip} ${section === key ? styles.chipActive : ''}`}
                data-testid={`flp-section-${key}`}
                data-active={section === key ? 'true' : 'false'}
                onClick={() => setSection(key)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {section === 'overview' && (
          <>
            <div className={styles.section}>
              <div
                style={{
                  display: 'flex',
                  gap: 12,
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <span
                  className={`${styles.badge} ${BADGE_TONE[detail.tone] ?? ''}`}
                >
                  {detail.stateLabel}
                </span>
                <span className={styles.strong} data-testid="flp-modal-next">
                  {detail.next.kind === 'countdown'
                    ? formatCountdown(detail.next.secondsRemaining, elapsed)
                    : detail.next.text}
                </span>
              </div>

              {detail.failure && (
                <div
                  className={`${styles.muted} ${styles.small}`}
                  style={{ marginBottom: 16 }}
                  data-testid="flp-failure-evidence"
                >
                  <span className={styles.checkFail}>
                    {detail.failure.label}
                  </span>
                  {' · '}error code <code>{detail.failure.errorCode}</code>
                  {' · '}run {detail.failure.runId} — quote this run id when
                  investigating. No further detail is shown here because the
                  backend did not report a safe, classified reason.
                </div>
              )}

              {detail.checks.length > 0 && (
                <div className={styles.checks} data-testid="flp-checks">
                  {detail.checks.map((c) => {
                    const obs = observedText(c);
                    return (
                      <div key={c.key} className={styles.check}>
                        <span className={c.passed ? styles.tick : styles.cross}>
                          {c.passed ? '✓' : '✕'}
                        </span>
                        <span
                          className={c.passed ? undefined : styles.checkFail}
                        >
                          {CHECK_LABELS[c.key] ?? c.key}
                          {obs && (
                            <span className={`${styles.muted} ${styles.small}`}>
                              {' '}
                              — {obs}
                            </span>
                          )}
                          {!c.passed && (
                            <span className={`${styles.muted} ${styles.small}`}>
                              {c.key === 'no_ops_offer'
                                ? '; First Responder did not act'
                                : ' — First Responder will not act'}
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {detail.funnel && (
              <div className={styles.section}>
                <Funnel funnel={detail.funnel} stage={detail.funnelStage} />
              </div>
            )}

            {/* Fix round 1 (Task 7 review, FINDING 3): the selected-count/list-count invariant is
                CHECKED, not assumed — a real persisted-data disagreement renders as a visible
                warning above the candidate list, never silently. Reuses the same bordered
                "attention" callout language as NeedsAttentionCard/the confirmation-stale banner. */}
            {detail.integrityWarning && (
              <div
                className={`${styles.section} ${styles.attention}`}
                data-testid="flp-job-integrity-warning"
              >
                <span className={styles.checkFail}>
                  {detail.integrityWarning}
                </span>
              </div>
            )}

            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Worker</th>
                    <th>Tier</th>
                    <th>Score</th>
                    <th>Distance</th>
                    <th>Decision</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.candidates.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className={styles.muted}
                        style={{ textAlign: 'center', padding: 24 }}
                      >
                        No candidates were selected for this job.
                      </td>
                    </tr>
                  ) : (
                    detail.candidates.map((c, i) => [
                      <tr key={`c${i}`} className={styles.zebra}>
                        <td className={styles.muted}>{c.rank}</td>
                        <td className={styles.strong}>
                          <button
                            type="button"
                            className={styles.btnPlain}
                            style={{
                              fontSize: 'inherit',
                              color: 'inherit',
                              fontWeight: 500,
                            }}
                            data-testid={`flp-candidate-${i}`}
                            aria-expanded={!!openSignals[i]}
                            onClick={() =>
                              setOpenSignals((s) => ({ ...s, [i]: !s[i] }))
                            }
                          >
                            {c.displayName}
                          </button>
                        </td>
                        <td>{c.tier ?? 'Unknown'}</td>
                        <td className={styles.muted}>{c.score ?? 'Unknown'}</td>
                        <td className={styles.muted}>
                          {c.distanceMiles !== null
                            ? `${c.distanceMiles} mi`
                            : 'Unknown'}
                        </td>
                        <td>
                          <span
                            className={`${styles.badge} ${BADGE_TONE[c.decisionTone] ?? ''}`}
                          >
                            {c.decisionLabel}
                          </span>
                        </td>
                      </tr>,
                      openSignals[i] ? (
                        <tr key={`s${i}`} data-testid={`flp-signals-${i}`}>
                          <td colSpan={6} style={{ padding: 0 }}>
                            <div className={styles.sig}>
                              {c.signals.map((s) => (
                                <div key={s.label}>
                                  <div className={styles.k}>{s.label}</div>
                                  <div className={styles.v}>{s.value}</div>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : null,
                    ])
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles.section}>
              <ul className={styles.timeline} data-testid="flp-timeline">
                {detail.timeline.map((t, i) => (
                  <li key={i}>
                    <span className={styles.t}>{t.at ?? '—'}</span>
                    <span>{t.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {section === 'workers' && (
          <>
            <div className={styles.section}>
              <div
                className={`${styles.muted} ${styles.small}`}
                data-testid="flp-candidates-total"
                style={{ marginBottom: 8 }}
              >
                Candidates: {candidatesTotalText}
              </div>
              <Funnel funnel={detail.funnel} stage={detail.funnelStage} />
              <details
                data-testid="flp-ranking-legend"
                style={{ marginTop: 8 }}
              >
                <summary
                  className={`${styles.muted} ${styles.small}`}
                  style={{ cursor: 'pointer' }}
                >
                  How this list is ordered
                </summary>
                <div className={`${styles.small}`} style={{ marginTop: 8 }}>
                  <p className={styles.muted} style={{ margin: 0 }}>
                    {ranking.intro}
                  </p>
                  <ol style={{ margin: '8px 0 0', paddingLeft: 20 }}>
                    {ranking.steps.map((step) => (
                      <li
                        key={step}
                        className={styles.muted}
                        style={{ marginTop: 4 }}
                      >
                        {step}
                      </li>
                    ))}
                  </ol>
                  <p className={styles.muted} style={{ margin: '8px 0 0' }}>
                    {ranking.scoreNote}
                  </p>
                  <p className={styles.muted} style={{ margin: '4px 0 0' }}>
                    {ranking.unknownNote}
                  </p>
                </div>
              </details>
            </div>
            <div className={styles.tableScroll}>
              <table className={styles.table} data-testid="flp-workers-table">
                <thead>
                  <tr>
                    <th>Worker</th>
                    <th>Tier</th>
                    <th>Score</th>
                    <th>Distance</th>
                    <th>Favorite</th>
                    <th>Shifts here</th>
                    <th>Selection</th>
                    <th>Why</th>
                  </tr>
                </thead>
                <tbody>
                  {workersPage.items.length === 0 ? (
                    <tr>
                      <td
                        colSpan={8}
                        className={styles.muted}
                        style={{ textAlign: 'center', padding: 24 }}
                      >
                        No recorded after-gates snapshot for this job.
                      </td>
                    </tr>
                  ) : (
                    workersPage.items.map((w) => (
                      <tr
                        key={w.id}
                        className={styles.zebra}
                        data-testid={`flp-worker-${w.id}`}
                      >
                        <td className={styles.strong}>
                          {hasWorkerPage(w.id) ? (
                            <Link
                              to={`/workers/${w.id}`}
                              className={styles.jobTitleLink}
                              title={`Open worker #${w.id}`}
                              data-testid={`flp-worker-link-${w.id}`}
                            >
                              {w.displayName}
                            </Link>
                          ) : (
                            w.displayName
                          )}
                        </td>
                        <td>{w.tier ?? 'Unknown'}</td>
                        <td className={styles.muted}>{w.score ?? 'Unknown'}</td>
                        <td className={styles.muted}>
                          {w.distanceMiles !== null
                            ? `${w.distanceMiles} mi`
                            : 'Unknown'}
                        </td>
                        <td className={styles.muted}>
                          {w.favorite ? '★ Favorite' : '—'}
                        </td>
                        <td className={styles.muted}>
                          {w.shiftsWithClient !== null
                            ? w.shiftsWithClient
                            : 'Unknown'}
                        </td>
                        <td>
                          {w.selectionRank !== null ? (
                            <span className={`${styles.badge} ${styles.green}`}>
                              SELECTED #{w.selectionRank}
                            </span>
                          ) : w.eligibility === 'unselectable_unknown' ? (
                            <span className={styles.muted}>Not selectable</span>
                          ) : (
                            <span className={styles.muted}>—</span>
                          )}
                        </td>
                        <td
                          className={`${styles.muted} ${styles.small} ${styles.whyCell}`}
                        >
                          {w.why}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <PreviewPagination
              pagination={workersPage.pagination}
              onPageChange={onWorkersPageChange}
              itemsLabel="workers"
            />
            {detail.exclusions.length > 0 && (
              <div className={styles.section}>
                <details data-testid="flp-exclusions">
                  <summary
                    className={styles.strong}
                    style={{ cursor: 'pointer' }}
                  >
                    Excluded before ranking
                  </summary>
                  <ul style={{ margin: '12px 0 0', paddingLeft: 18 }}>
                    {detail.exclusions.map((g) => (
                      <li key={g.key} data-testid={`flp-exclusion-${g.key}`}>
                        {g.supported && g.count !== null ? (
                          <>
                            <span className={styles.strong}>{g.count}</span>{' '}
                            <span className={styles.muted}>— {g.label}</span>
                          </>
                        ) : (
                          <span className={styles.muted}>
                            {g.label} — <em>{unsupportedExclusionText(g)}</em>
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </details>
              </div>
            )}
          </>
        )}

        {/* RECORDED-RESULT evidence: job id, run id, evaluated time, policy
            version — and the immutability language. */}
        <div className={`${styles.section} ${styles.sectionSubdued}`}>
          <span
            className={`${styles.muted} ${styles.small}`}
            data-testid="flp-run-evidence"
          >
            Recorded result — job #{detail.jobId} · run {detail.runMeta.runId} ·
            evaluated {detail.runMeta.evaluatedAt} · policy{' '}
            {detail.runMeta.policyVersion}. Recorded runs are never re-ranked;
            policy changes apply to future evaluations only.
            {evidence?.recordedAt
              ? ` Checks recorded ${evidence.recordedAt}.`
              : ''}
          </span>
        </div>
      </div>
    </div>
  );
};

export default JobModal;
